// src/services/api.js (Version Axios - UNIFIÉE avec endpoints conservés)
import axios from 'axios';

// Configuration pour l'environnement de développement avec proxy
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Création d'une instance Axios
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
});

// Intercepteur pour les requêtes - Ajoute automatiquement le token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('❌ Erreur intercepteur requête:', error);
    return Promise.reject(error);
  }
);

// Intercepteur pour les réponses
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('❌ Erreur intercepteur réponse:', error.response?.status, error.message);
    
    // if (error.response?.status === 401) {
    //   localStorage.removeItem('authToken');
    //   window.location.href = '/login';
    // }
    
    return Promise.reject(error);
  }
);

// Fonction pour obtenir la configuration avec token
const getAuthConfig = (token) => {
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

// ================ AUTHENTIFICATION ================

export const authenticate = async (credentials) => {
  try {
    const response = await apiClient.post('/auth/login', credentials);
    const data = response.data;

    if (data.token) {
      return data.token;
    } else if (data.access_token) {
      return data.access_token;
    } else {
      throw new Error('Token non trouvé dans la réponse');
    }
  } catch (error) {
    console.error('❌ Erreur d\'authentification:', error);
    throw error;
  }
};

// ================ CONTACTS ================

export const fetchContactsList = async (token) => {
  try {
    const response = await apiClient.get('/contacts', getAuthConfig(token));
    const data = response.data;
    
    if (data.contacts && Array.isArray(data.contacts)) {
      return data.contacts;
    } else if (data.data !== undefined && Array.isArray(data.data)) {
      return data.data;
    } else if (Array.isArray(data)) {
      return data;
    } else if (data.messages !== undefined && Array.isArray(data.messages)) {
      return data.messages;
    } else if (data.items !== undefined && Array.isArray(data.items)) {
      return data.items;
    } else {
      console.warn('⚠️ Structure de réponse inattendue:', data);
      const arrayKeys = Object.keys(data).filter(key => Array.isArray(data[key]));
      if (arrayKeys.length > 0) {
        return data[arrayKeys[0]];
      }
      return [];
    }
  } catch (error) {
    console.error('❌ Erreur fetchContactsList:', error);
    throw error;
  }
};

export const addContact = async (token, contactData) => {
  try {
    const response = await apiClient.post('/contacts', contactData, getAuthConfig(token));
    return response.data;
  } catch (error) {
    console.error('❌ Erreur addContact:', error);
    
    if (error.response) {
      switch (error.response.status) {
        case 400: throw new Error('Données invalides pour la création du contact');
        case 401: throw new Error('Token d\'authentification invalide ou expiré');
        case 409: throw new Error('Un contact avec ce numéro de téléphone existe déjà');
        case 422: throw new Error('Erreur de validation des données');
        case 500: throw new Error('Erreur interne du serveur');
        default: throw new Error(`Erreur serveur: ${error.response.status}`);
      }
    } else if (error.request) {
      throw new Error('Impossible de contacter le serveur');
    }
    throw error;
  }
};

export const updateContact = async (token, contactId, contactData) => {
  try {
    const response = await apiClient.put(`/contacts/${contactId}`, contactData, getAuthConfig(token));
    return response.data;
  } catch (error) {
    console.error('❌ Erreur updateContact:', error);
    
    if (error.response) {
      switch (error.response.status) {
        case 400: throw new Error('Données invalides');
        case 401: throw new Error('Session expirée');
        case 404: throw new Error('Contact non trouvé');
        case 500: throw new Error('Erreur serveur');
        default: throw new Error(`Erreur ${error.response.status}`);
      }
    } else if (error.code === 'ECONNABORTED') {
      throw new Error('Timeout - Serveur non répondant');
    }
    throw error;
  }
};

export const deleteContact = async (token, contactId) => {
  try {
    const response = await apiClient.delete(`/contacts/${contactId}`, getAuthConfig(token));
    
    let isSuccess = false;
    if (response.status === 200 || response.status === 204) {
      isSuccess = true;
    } else if (response.data && (response.data.success || response.data.code === 200)) {
      isSuccess = true;
    }

    return {
      code: response.status,
      success: isSuccess,
      message: 'Contact supprimé avec succès',
      data: response.data
    };
  } catch (error) {
    console.error('❌ Erreur deleteContact:', error);
    
    if (error.response) {
      switch (error.response.status) {
        case 401: throw new Error('Token invalide ou expiré');
        case 403: throw new Error('Permission refusée');
        case 404: throw new Error('Contact non trouvé');
        case 500: throw new Error('Erreur serveur');
        default: throw new Error(`Erreur ${error.response.status}`);
      }
    }
    throw error;
  }
};

export const fetchContactGroups = async (token) => {
  try {
    const response = await apiClient.get('/contact-groups', getAuthConfig(token));
    const result = response.data;
    
    if (result.code === 200 && Array.isArray(result.data)) {
      return result.data;
    } else if (Array.isArray(result)) {
      return result;
    } else {
      throw new Error('Format de réponse invalide pour les groupes');
    }
  } catch (error) {
    console.error('❌ Erreur fetchContactGroups:', error);
    throw error;
  }
};

// ================ ENTITÉS ================

export const fetchEntitesList = async (token) => {
  try {
    const response = await apiClient.get('/entites', getAuthConfig(token));
    const data = response.data;
    
    if (Array.isArray(data)) {
      return data;
    } else if (data.data && Array.isArray(data.data)) {
      return data.data;
    } else {
      console.warn('⚠️ Format de données inattendu:', data);
      return [];
    }
  } catch (error) {
    console.error('❌ Erreur fetchEntitesList:', error);
    throw error;
  }
};

export const addEntite = async (token, entiteData) => {
  try {
    const response = await apiClient.post('/entites', entiteData, getAuthConfig(token));
    return response.data;
  } catch (error) {
    console.error('❌ Erreur addEntite:', error);
    
    if (error.response) {
      switch (error.response.status) {
        case 400: throw new Error('Données invalides pour la création de l\'entité');
        case 401: throw new Error('Token d\'authentification invalide ou expiré');
        case 409: throw new Error('Une entité avec cet email existe déjà');
        default: throw new Error(`Erreur ${error.response.status}`);
      }
    }
    throw error;
  }
};

export const updateEntite = async (token, entiteId, entiteData) => {
  try {
    const response = await apiClient.put(`/entites/${entiteId}`, entiteData, getAuthConfig(token));
    return response.data;
  } catch (error) {
    console.error('❌ Erreur updateEntite:', error);
    
    if (error.response) {
      switch (error.response.status) {
        case 400: throw new Error('Données invalides pour la mise à jour');
        case 401: throw new Error('Token d\'authentification invalide ou expiré');
        case 404: throw new Error('Entité non trouvée');
        case 500: throw new Error('Erreur serveur lors de la mise à jour');
        default: throw new Error(`Erreur ${error.response.status}`);
      }
    }
    throw error;
  }
};

export const deleteEntite = async (token, entiteId) => {
  try {
    const response = await apiClient.delete(`/entites/${entiteId}`, getAuthConfig(token));
    return response.data || { success: true, message: 'Entité supprimée avec succès' };
  } catch (error) {
    console.error('❌ Erreur deleteEntite:', error);
    
    if (error.response) {
      switch (error.response.status) {
        case 401: throw new Error('Token d\'authentification invalide ou expiré');
        case 404: throw new Error('Entité non trouvée');
        default: throw new Error(`Erreur ${error.response.status}`);
      }
    }
    throw error;
  }
};

// ================ SENDER IDs ================

export const fetchSenderList = async (token) => {
  try {
    const response = await apiClient.get('/senderid', getAuthConfig(token));
    const data = response.data;
    
    if (data.data !== undefined) {
      return data.data;
    } else if (Array.isArray(data)) {
      return data;
    } else if (data.messages !== undefined) {
      return data.messages;
    } else if (data.items !== undefined) {
      return data.items;
    } else {
      console.warn('⚠️ Structure de réponse inattendue:', data);
      return [];
    }
  } catch (error) {
    console.error('❌ Erreur fetchSenderList:', error);
    throw error;
  }
};

export const addSender = async (token, senderData) => {
  try {
    const response = await apiClient.post('/sender/add', senderData, getAuthConfig(token));
    return response.data;
  } catch (error) {
    console.error('❌ Erreur addSender:', error);
    
    if (error.response) {
      switch (error.response.status) {
        case 400: throw new Error('Données invalides');
        case 401: throw new Error('Token invalide ou expiré');
        case 403: throw new Error('Permission refusée');
        case 409: throw new Error('Ce Sender ID existe déjà');
        case 422: throw new Error('Erreur de validation');
        case 429: throw new Error('Trop de tentatives');
        default: throw new Error(`Erreur HTTP: ${error.response.status}`);
      }
    } else if (error.request) {
      throw new Error('Impossible de contacter le serveur');
    }
    throw error;
  }
};

export const deleteSender = async (token, senderId) => {
  try {
    const response = await apiClient.delete(`/sender/${senderId}`, getAuthConfig(token));
    return response.data;
  } catch (error) {
    console.error('❌ Erreur deleteSender:', error);
    
    if (error.response) {
      switch (error.response.status) {
        case 401: throw new Error('Token invalide ou expiré');
        case 403: throw new Error('Permission refusée');
        case 404: throw new Error('Sender ID non trouvé');
        default: throw new Error(`Erreur HTTP: ${error.response.status}`);
      }
    }
    throw error;
  }
};

export const updateSenderStatus = async (token, senderId, requestBody) => {
  try {
    const response = await apiClient.put(`/sender/activate/${senderId}`, requestBody, getAuthConfig(token));
    return response.data;
  } catch (error) {
    console.error('❌ Erreur updateSenderStatus:', error);
    
    if (error.response) {
      switch (error.response.status) {
        case 400: throw new Error('Données invalides');
        case 401: throw new Error('Token invalide ou expiré');
        case 404: throw new Error('Sender ID non trouvé');
        default: throw new Error(`Erreur ${error.response.status}`);
      }
    }
    throw error;
  }
};

// ================ SMS ================

export const fetchSMSList = async (token) => {
  try {
    const response = await apiClient.get('/sms/list', getAuthConfig(token));
    const data = response.data;
    
    if (data.data !== undefined) {
      return data.data;
    } else if (Array.isArray(data)) {
      return data;
    } else if (data.messages !== undefined) {
      return data.messages;
    } else if (data.items !== undefined) {
      return data.items;
    } else {
      console.warn('⚠️ Structure de réponse inattendue:', data);
      return [];
    }
  } catch (error) {
    console.error('❌ Erreur fetchSMSList:', error);
    throw error;
  }
};

// ================ TRANSACTIONS ================

export const fetchTransactionsList = async (token) => {
  try {
    const response = await apiClient.get('/transactions', getAuthConfig(token));
    const data = response.data;
    
    if (data.payments && Array.isArray(data.payments)) {
      return data.payments;
    } else if (data.data !== undefined && Array.isArray(data.data)) {
      return data.data;
    } else if (Array.isArray(data)) {
      return data;
    } else if (data.messages !== undefined && Array.isArray(data.messages)) {
      return data.messages;
    } else if (data.items !== undefined && Array.isArray(data.items)) {
      return data.items;
    } else {
      console.warn('⚠️ Structure de réponse inattendue:', data);
      const arrayKeys = Object.keys(data).filter(key => Array.isArray(data[key]));
      if (arrayKeys.length > 0) {
        return data[arrayKeys[0]];
      }
      return [];
    }
  } catch (error) {
    console.error('❌ Erreur fetchTransactionsList:', error);
    throw error;
  }
};

// ================ PRICING / TARIFS ================

export const fetchPricingList = async (token) => {
  try {
    const response = await apiClient.get('/pricing/list', getAuthConfig(token));
    const data = response.data;
    
    if (data.data !== undefined && Array.isArray(data.data)) {
      return data.data;
    } else if (data.pricing && Array.isArray(data.pricing)) {
      return data.pricing;
    } else if (Array.isArray(data)) {
      return data;
    } else if (data.items !== undefined && Array.isArray(data.items)) {
      return data.items;
    } else if (data.tarifs !== undefined && Array.isArray(data.tarifs)) {
      return data.tarifs;
    } else {
      console.warn('⚠️ Structure de réponse inattendue:', data);
      const arrayKeys = Object.keys(data).filter(key => Array.isArray(data[key]));
      if (arrayKeys.length > 0) {
        return data[arrayKeys[0]];
      }
      return [];
    }
  } catch (error) {
    console.error('❌ Erreur fetchPricingList:', error);
    
    if (error.response) {
      switch (error.response.status) {
        case 401: throw new Error('Token d\'authentification invalide ou expiré');
        case 403: throw new Error('Accès non autorisé aux tarifs');
        case 404: throw new Error('Aucun tarif trouvé');
        case 500: throw new Error('Erreur interne du serveur');
        default: throw new Error(`Erreur serveur: ${error.response.status}`);
      }
    } else if (error.request) {
      throw new Error('Impossible de contacter le serveur');
    }
    throw error;
  }
};

export const formatPricingData = (tarif) => ({
  id: tarif.id,
  dateCreation: new Date().toLocaleDateString('fr-FR'),
  type: "SMS",
  reference: `TARIF-${tarif.country}-${tarif.network}`,
  statut: "Actif",
  montant: `${tarif.sms_pricing} ${tarif.currency}`,
  sms_pricing: tarif.sms_pricing,
  dev_price: tarif.dev_price,
  country: tarif.country,
  network: tarif.network,
  currency: tarif.currency,
  business_id: tarif.business_id
});

// ================ ALERTES ================

export const fetchAlertesList = async (token) => {
  try {
    const response = await apiClient.get('/hist-alertes', getAuthConfig(token));
    return response.data;
  } catch (error) {
    console.error('❌ Erreur fetchAlertesList:', error);
    throw error;
  }
};

export const fetchAlerteMarchand = async (token) => {
  try {
    const response = await apiClient.get('/alertes', getAuthConfig(token));
    return response.data;
  } catch (error) {
    console.error('❌ Erreur fetchAlerteMarchand:', error);
    throw error;
  }
};

// ================ WEBHOOKS ================

export const fetchWebhooksList = async (token) => {
  try {
    const response = await apiClient.get('/sms-webhooks', getAuthConfig(token));
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erreur lors du chargement des webhooks');
  }
};

export const createWebhook = async (token, webhookData) => {
  try {
    const response = await apiClient.post('/sms-webhooks/create', webhookData, getAuthConfig(token));
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erreur lors de la création du webhook');
  }
};

export const updateWebhook = async (token, webhookId, webhookData) => {
  try {
    const response = await apiClient.put(`/sms-webhooks/${webhookId}`, webhookData, getAuthConfig(token));
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erreur lors de la mise à jour du webhook');
  }
};

export const deleteWebhook = async (token, webhookId) => {
  try {
    const response = await apiClient.delete(`/sms-webhooks/${webhookId}`, getAuthConfig(token));
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erreur lors de la suppression du webhook');
  }
};

export const testWebhook = async (token, webhookId, payload) => {
  try {
    const response = await apiClient.post(`/sms-webhooks/${webhookId}/test`, payload, getAuthConfig(token));
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erreur lors du test du webhook');
  }
};

// ================ GESTION GSM ================

const extractArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  console.warn('⚠️ Format de données inattendu:', data);
  return [];
};

const gsmError = (error, fallback) =>
  new Error(error.response?.data?.message || fallback);

// Passerelles
export const fetchGsmGateways = async (token) => {
  try {
    const response = await apiClient.get('/gsm/gateways', getAuthConfig(token));
    return extractArray(response.data);
  } catch (error) {
    throw gsmError(error, 'Erreur lors du chargement des passerelles GSM');
  }
};

export const addGsmGateway = async (token, gatewayData) => {
  try {
    const response = await apiClient.post('/gsm/gateways', gatewayData, getAuthConfig(token));
    return response.data;
  } catch (error) {
    throw gsmError(error, 'Erreur lors de la création de la passerelle');
  }
};

export const updateGsmGateway = async (token, gatewayId, gatewayData) => {
  try {
    const response = await apiClient.put(`/gsm/gateways/${gatewayId}`, gatewayData, getAuthConfig(token));
    return response.data;
  } catch (error) {
    throw gsmError(error, 'Erreur lors de la mise à jour de la passerelle');
  }
};

// Localisations (liste de référence des emplacements de passerelles)
export const fetchGsmLocations = async (token) => {
  try {
    const response = await apiClient.get('/gsm/locations', getAuthConfig(token));
    return extractArray(response.data)
      .map(l => (typeof l === 'string' ? l : l?.name || l?.label || l?.location || ''))
      .filter(Boolean);
  } catch (error) {
    throw gsmError(error, 'Erreur lors du chargement des localisations');
  }
};

// Affectations (réservation d'un port de passerelle par un client)
export const fetchGsmAssignments = async (token) => {
  try {
    const response = await apiClient.get('/gsm/assignments', getAuthConfig(token));
    return extractArray(response.data);
  } catch (error) {
    throw gsmError(error, 'Erreur lors du chargement des affectations');
  }
};

export const addGsmAssignment = async (token, assignmentData) => {
  try {
    const response = await apiClient.post('/gsm/assignments', assignmentData, getAuthConfig(token));
    return response.data;
  } catch (error) {
    throw gsmError(error, 'Erreur lors de la réservation du port');
  }
};

export const deleteGsmAssignment = async (token, assignmentId) => {
  try {
    const response = await apiClient.delete(`/gsm/assignments/${assignmentId}`, getAuthConfig(token));
    return response.data;
  } catch (error) {
    throw gsmError(error, 'Erreur lors de la libération du port');
  }
};

// URLs clients (callback + authentification)
export const fetchGsmClients = async (token) => {
  try {
    const response = await apiClient.get('/gsm/clients', getAuthConfig(token));
    return extractArray(response.data);
  } catch (error) {
    throw gsmError(error, 'Erreur lors du chargement des configurations clients');
  }
};

export const saveGsmClientUrls = async (token, businessId, urls) => {
  try {
    const response = await apiClient.put(`/gsm/clients/${businessId}`, urls, getAuthConfig(token));
    return response.data;
  } catch (error) {
    throw gsmError(error, 'Erreur lors de l\'enregistrement des URLs');
  }
};

// Historique des SMS GSM (gsm_clean_sms) — filtres cumulatifs côté API
export const fetchGsmSmsHistory = async (token, params = {}) => {
  try {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '')
    );
    const response = await apiClient.get('/gsm/sms', { ...getAuthConfig(token), params: cleanParams });
    const body = response.data || {};
    return {
      data: Array.isArray(body.data) ? body.data : [],
      meta: body.meta || { current_page: 1, last_page: 1, per_page: 25, total: 0 },
      types: Array.isArray(body.types) ? body.types : []
    };
  } catch (error) {
    throw gsmError(error, 'Erreur lors du chargement de l\'historique des SMS');
  }
};

// ================ EXPORT ================

export { API_BASE_URL, apiClient };